/**
 * Whether the admin API is reachable.
 *
 * The dashboard treats a 503 from the admin surface as "the backend is having a
 * problem" and shows an error screen. Nothing on the client decides this — it
 * only reflects what the server answered.
 *
 * Two ways the state arrives:
 *   1. A probe on boot (GET /admin/health), which still answers when the rest
 *      of the admin surface does not.
 *   2. Any admin request that comes back 503 — which is what covers a tab that
 *      was already open when the backend went down.
 */

export interface ServiceStatus {
  unavailable: boolean
}

let state: ServiceStatus = { unavailable: false }
const listeners = new Set<(next: ServiceStatus) => void>()

/**
 * Counts 503s arriving from live requests. The boot probe reads it before and
 * after its own round trip: if a 503 landed while that request was in flight,
 * the probe's older "all clear" is stale and must not undo it.
 */
let unavailableSignals = 0

const publish = (next: ServiceStatus) => {
  // Ignore no-op updates so a screen full of failing requests does not cause a
  // render per request.
  if (state.unavailable === next.unavailable) {
    return
  }
  state = next
  listeners.forEach((fn) => fn(state))
}

export const getServiceStatus = (): ServiceStatus => state

export const subscribeServiceStatus = (fn: (next: ServiceStatus) => void): (() => void) => {
  listeners.add(fn)
  return () => {
    listeners.delete(fn)
  }
}

/** Called by the API interceptor when an admin request is answered with a 503. */
export const reportServiceUnavailable = (): void => {
  unavailableSignals += 1
  publish({ unavailable: true })
}

/**
 * Boot probe. Deliberately uses `fetch` rather than the shared axios client:
 * that client attaches the auth token and owns the 401 redirect, and this call
 * must work for a signed-out visitor without tripping either.
 *
 * Only a 503 closes the portal. A network failure does NOT — a flaky connection
 * or a blocked request must not lock an operator out of a healthy system, and
 * if the backend really is down every subsequent request returns the 503 and
 * flips the app over anyway.
 */
export const probeServiceStatus = async (baseUrl: string): Promise<ServiceStatus> => {
  const signalsAtStart = unavailableSignals

  try {
    const response = await fetch(`${baseUrl}/admin/health`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    })

    if (response.status === 503) {
      reportServiceUnavailable()
    } else if (unavailableSignals === signalsAtStart) {
      publish({ unavailable: false })
    }
  } catch {
    if (unavailableSignals === signalsAtStart) {
      publish({ unavailable: false })
    }
  }

  return state
}
